import type {
  CreateAssignmentFormDTO,
  UpdateAssignmentFormDTO,
} from "@/modules/assign-position/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";

const baseUrl = `${config.apiUrl}/assignments`;

export const assignmentsApi = {
  async listUsers(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}/users?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch users: ${res.statusText}`);
    return res.json();
  },

  async get(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch assignment: ${res.statusText}`);
    return res.json();
  },

  async getUserHistory(userId: string) {
    const res = await fetch(`${baseUrl}/users/${userId}/history`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch user history: ${res.statusText}`);
    return res.json();
  },

  async create(input: CreateAssignmentFormDTO) {
    const formData = new FormData();
    formData.set("positionId", input.positionId);
    formData.set("userId", input.userId);
    if (input.startDate) {
      formData.set("startDate", input.startDate);
    }
    // Send isPrimary as "on" if true (checkbox format)
    // The schema will transform "on" to boolean true
    if (input.isPrimary) {
      formData.set("isPrimary", "on");
    }
    if (input.salaryGradeId?.trim()) {
      formData.set("salaryGradeId", input.salaryGradeId.trim());
    }
    if (input.notes) {
      formData.set("notes", input.notes);
    }

    const res = await fetch(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create assignment: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async update(userId: string, input: UpdateAssignmentFormDTO) {
    const formData = new FormData();
    formData.set("positionId", input.positionId);
    if (input.startDate) {
      formData.set("startDate", input.startDate);
    }
    if (input.salaryGradeId?.trim()) {
      formData.set("salaryGradeId", input.salaryGradeId.trim());
    }
    if (input.notes) {
      formData.set("notes", input.notes);
    }

    const res = await fetch(`${baseUrl}/users/${userId}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update assignment: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async clearCurrentAssignment(userId: string) {
    const res = await fetch(`${baseUrl}/users/${userId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to clear assignment: ${res.statusText}`,
      );
    }
    return res.json();
  },
};
